import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import PlanStatusIcon from '../badges/PlanStatusIcon'
import TaskCountBadge from '../badges/TaskCountBadge'
import { resolveItemTaskIds } from '../helpers'
import PlanItemRow from './PlanItemRow'
import DescriptionTooltip from './DescriptionTooltip'

const summaryClasses = { root: '__summary-root', content: '__summary-content' }
const summaryClassesExpanded = { root: '__summary-root-expanded', content: '__summary-content' }
const detailsClasses = { root: '__details-root' }

function PlanCategoryGroup ({
  category,
  statusStyleOverrides,
  selectedItemId,
  onSelectItem,
  defaultExpanded
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const isSelected = selectedItemId === category.id

  // Re-sync expand state when the active filter changes upstream and this
  // category now has matching items worth surfacing.
  useEffect(() => {
    setExpanded(defaultExpanded)
  }, [defaultExpanded])

  const handleChange = (_e, isExpanded) => setExpanded(isExpanded)

  // Clicking the row content opens the side panel and force-expands the
  // accordion — never collapses. Collapsing is exclusively the chevron's job.
  const handleSummaryClick = (e) => {
    e.stopPropagation()
    setExpanded(true)
    onSelectItem(category.id)
  }

  const handleSummaryKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      setExpanded(true)
      onSelectItem(category.id)
    }
  }

  const categoryTaskCount = resolveItemTaskIds(category)?.length ?? 0

  return (
    <div className={clsx('__group-wrapper', { __selected: isSelected })}>
      <Accordion
        className='__accordion w-full'
        expanded={expanded}
        onChange={handleChange}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          classes={expanded ? summaryClassesExpanded : summaryClasses}
        >
          <div
            role='button'
            tabIndex={0}
            className='__group flex flex-row items-center w-full font-bold'
            onClick={handleSummaryClick}
            onKeyDown={handleSummaryKeyDown}
          >
            <div className='__status flex items-center'>
              <PlanStatusIcon status={category.status} size={23} statusStyleOverrides={statusStyleOverrides} />
            </div>
            <div className='flex flex-col flex-1 py-2' style={{ minWidth: 0 }}>
              <div className='flex items-center gap-xs'>
                <span className='__name-main'>{category.name}</span>
                <DescriptionTooltip content={category.information} />
              </div>
              {category.description ? (
                <span className='__subtitle font-size-xs font-light'>{category.description}</span>
              ) : null}
            </div>
            <TaskCountBadge count={categoryTaskCount} />
          </div>
        </AccordionSummary>
        <AccordionDetails classes={detailsClasses}>
          <div className='flex flex-col w-full'>
            {(category.data ?? []).map(item => (
              <PlanItemRow
                key={item.id}
                item={item}
                isSelected={selectedItemId === item.id}
                statusStyleOverrides={statusStyleOverrides}
                onSelect={onSelectItem}
              />
            ))}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

PlanCategoryGroup.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    status: PropTypes.string,
    description: PropTypes.string,
    information: PropTypes.string,
    data: PropTypes.array
  }).isRequired,
  statusStyleOverrides: PropTypes.object,
  selectedItemId: PropTypes.string,
  onSelectItem: PropTypes.func.isRequired,
  defaultExpanded: PropTypes.bool
}

PlanCategoryGroup.defaultProps = {
  defaultExpanded: false
}

export default PlanCategoryGroup
